/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { ButtonIcon, Typography } from "./..";
import OptionWrapper from "../others/option-wrapper";
import PropTypes from 'prop-types';
import { forwardRef, memo, useContext, useImperativeHandle, useMemo, useRef } from 'react';
import { bgColor, boxBorder, cursorMove, displayFlex, flexRow, itemsCenter, justifyBetween, mgl, order, parseWidth, parseWidthHeight, positionRelative, truncate } from "../../styles/general";
import { useTheme } from "../../theme";
import { classNames, handleBreakpoints } from "../../utils";
import ModalContext from "./context";
const {
  boxShadows: {
    small: smallBoxShadow
  },
  typography: {
    heading3
  },
  spacing
} = useTheme();
export const responsivePaddingCSS = handleBreakpoints({
  xs: 4,
  md: 6
}, propValue => {
  return `padding: ${spacing([4, propValue])};`;
});
const ModalHeader = /*#__PURE__*/memo( /*#__PURE__*/forwardRef(({
  action = {},
  children,
  className,
  id,
  showClose,
  style,
  title
}, reference) => {
  if (!reference) reference = useRef(null);
  const modal = useContext(ModalContext);
  const ref = useRef(null);
  const _ModalHeaderCSS = ModalHeaderCSS(modal);
  useImperativeHandle(reference, () => {
    const currentRef = ref.current || {};
    currentRef.element = ref.current;
    const _instance = {
      ...action
    }; // methods
    _instance.__proto__ = {}; // hidden methods
    currentRef.instance = _instance;
    return currentRef;
  });
  const renderTitle = content => {
    if (!content) return null;
    return jsx(Typography, {
      css: [truncate, parseWidth('100%')],
      fullWidth: false,
      type: 'h3'
    }, content);
  };
  return useMemo(() => {
    return jsx("div", {
      css: _ModalHeaderCSS,
      ref: ref,
      id: id,
      className: classNames('DGN-UI-Modal-Header', className),
      style: style,
      onMouseDown: modal.onMoveModal
    }, typeof children === 'string' ? renderTitle(children) : children, title || showClose ? jsx("div", {
      css: [TitleContainerCSS, title && parseWidth('100%')],
      className: 'DGN-UI-Modal-Header-Title'
    }, renderTitle(title), showClose ? jsx(ButtonIcon, {
      css: mgl([children || title ? 4 : 'auto']),
      viewType: 'ghost',
      name: 'Close',
      onClick: modal.close
    }) : null) : null);
  }, [children, className, id, showClose, style, title]);
}));
const ModalHeaderCSS = modal => css`
    ${displayFlex};
    ${flexRow};
    ${positionRelative};
    ${justifyBetween};
    ${itemsCenter};
    ${boxBorder};
    ${heading3};
    ${parseWidthHeight('100%', 56)};
    ${bgColor('fill/headerbar')}
    ${modal.onMoveModal && cursorMove};
    ${order(1)};
    border-radius: 4px 4px 0px 0px;
    box-shadow: ${smallBoxShadow};
    ${responsivePaddingCSS};
`;
const TitleContainerCSS = css`
    ${displayFlex};
    ${flexRow};
    ${positionRelative};
    ${justifyBetween};
    ${itemsCenter};
`;
ModalHeader.defaultProps = {
  className: '',
  showClose: true,
  style: {},
  title: ''
};
ModalHeader.propTypes = {
  /** The content of the component. */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** Class for component. */
  className: PropTypes.string,
  /** If `true`, button close is shown. */
  showClose: PropTypes.bool,
  /** Style inline of component. */
  style: PropTypes.object,
  /** The content of the component if not have prop `children`. */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};
export { ModalHeader };
export default OptionWrapper(ModalHeader);